import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Select,
  Input,
  InputNumber,
  Space,
  Tag,
  Checkbox,
  message,
  Spin,
  Tooltip
} from 'antd';
import styles from '@/common/Layout/index.module.less';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPreviousStageBatches,
  getContainersForVerification,
  scanResolveContainer,
  submitSplit,
  generateVerificationPdf,
  getVerificationTolerance,
  resetProductVerification
} from '../../redux/slice';
import {
  getAllDispenseBooths,
  getBalance
} from '@/features/dispensing/redux/slice';
import { useInitiateVerificationMutation } from '@/features/logBooks/redux/apiSlice';
import Layout from '@/common/Layout';
import FormItem from '@/common/FormItem';
import CustomButton from '@/common/CustomButton';
import CustomTable from 'common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
// import { getLoggedInUser } from 'utilities/helpers';
import { history } from 'app/history';
import { useParams, useLocation } from 'react-router-dom';
import { QrReader } from 'react-qr-reader';
import { v4 as uuidv4 } from 'uuid';
import {
  QrcodeOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  PrinterOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

const ProductVerificationForm = () => {
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const dispatch = useDispatch();
  const { id: route_id } = useParams();
  const location = useLocation();
  // /product-verification/lc/:id  → compression LC anchor
  // /product-verification/:id     → dispensing-request anchor (default)
  const is_lc_mode = /\/product-verification\/lc\//.test(
    location?.pathname || ''
  );
  const dispensing_request_id = is_lc_mode ? null : route_id;
  const lc_bmr_eqp_id = is_lc_mode ? route_id : null;
  // const { user } = getLoggedInUser();

  const [initiateVerification, { isLoading: isVerifying }] =
    useInitiateVerificationMutation();

  const {
    batchesLoading,
    containersLoading,
    submitting,
    previousBatches,
    packingBatchNumber,
    currentStage,
    containers,
    persistedBalanceName,
    tolerance,
    snapshotSource,
    snapshotCapturedAt
  } = useSelector((state) => state.productVerification);

  // Local state
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [verificationState, setVerificationState] = useState({});

  // eslint-disable-next-line no-unused-vars
  const [isVerified, setIsVerified] = useState(false);
  const [verificationUniqueId, setVerificationUniqueId] = useState('');

  const [scanModalVisible, setScanModalVisible] = useState(false);
  const [scanningContainerId, setScanningContainerId] = useState(null);
  const [qaModalVisible, setQaModalVisible] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  // Balance + per-row weight state (mirrors material-verification)
  const [dispenseBooths, setDispenseBooths] = useState([]);
  const [balanceName, setBalanceName] = useState();
  const [balanceMeta, setBalanceMeta] = useState({
    ip: null,
    port: null,
    type: null
  });
  const [balanceScanVisible, setBalanceScanVisible] = useState(false);
  const [readingForContainer, setReadingForContainer] = useState(null);

  // Fetch previous-stage batches (with full details from prod label gen) on mount
  useEffect(() => {
    if (lc_bmr_eqp_id) {
      dispatch(getPreviousStageBatches({ lc_bmr_eqp_id }));
    } else if (dispensing_request_id) {
      dispatch(getPreviousStageBatches({ dispensing_request_id }));
    }
    return () => {
      dispatch(resetProductVerification());
    };
  }, [dispensing_request_id, lc_bmr_eqp_id]);

  // Load dispense booths once for the Balance Name dropdown
  useEffect(() => {
    dispatch(getAllDispenseBooths()).then((result) => {
      if (result?.payload?.success) {
        setDispenseBooths(result?.payload?.data || []);
      }
    });
    dispatch(
      getVerificationTolerance({ verification_type: 'product_verification' })
    );
  }, []);

  const handleBalanceSelect = (selectedName) => {
    setBalanceName(selectedName);
    const matched = dispenseBooths?.find(
      (b) => b?.balance_name === selectedName
    );
    setBalanceMeta({
      ip: matched?.ip_address || null,
      port: matched?.port || null,
      type: matched?.type || null
    });
  };

  const handleBalanceQrScan = (result) => {
    if (result?.text) {
      const scanned = result.text.trim();
      const matched = dispenseBooths?.find((b) => b?.balance_name === scanned);
      if (matched) {
        handleBalanceSelect(scanned);
        message.success(`Balance ${scanned} selected.`);
      } else {
        message.error(`Balance "${scanned}" not found.`);
      }
      setBalanceScanVisible(false);
    }
  };

  const handleReadGrossWeight = (record) => {
    if (!balanceName || !balanceMeta.ip) {
      message.warning('Please select a Balance Name first.');
      return;
    }
    setReadingForContainer(record.container_id);
    dispatch(
      getBalance({
        ip: balanceMeta.ip,
        type: balanceMeta.type,
        port: balanceMeta.port
      })
    )
      .then((response) => {
        if (response?.payload?.success) {
          const reading = response?.payload?.data;
          handleEnteredGrossWeight(record, reading);
          message.success(`Read ${reading} from ${balanceName}.`);
        } else {
          message.error(
            response?.payload?.message || 'Unable to read from balance.'
          );
        }
      })
      .finally(() => setReadingForContainer(null));
  };

  // Compute one of: 'match' | 'within' | 'beyond' | null
  const computeGrossStatus = (storedRaw, enteredRaw) => {
    const stored = parseFloat(storedRaw);
    const entered = parseFloat(enteredRaw);
    if (Number.isNaN(stored) || Number.isNaN(entered)) return null;
    if (stored === entered) return 'match';
    const tolerancePct = parseFloat(tolerance?.tolerance_percentage) || 0;
    if (tolerancePct <= 0) return 'beyond';
    const allowed = (stored * tolerancePct) / 100;
    return Math.abs(entered - stored) <= allowed ? 'within' : 'beyond';
  };

  const handleEnteredGrossWeight = (record, value) => {
    const status = computeGrossStatus(record?.gross_weight, value);
    setVerificationState((prev) => ({
      ...prev,
      [record.container_id]: {
        ...prev[record.container_id],
        gross_wt_scanned: value != null ? String(value) : '',
        gross_status: status,
        gross_wt_verified: status === 'match' || status === 'within'
      }
    }));
  };

  // Auto-select when only one entry exists
  useEffect(() => {
    if (previousBatches.length === 1 && !selectedEntry) {
      handleEntrySelect(previousBatches[0].prod_dispenses_id);
    }
  }, [previousBatches]);

  // Seed Balance Name from any persisted verification once the booth list and the
  // persisted name are both available; user may still edit afterward.
  useEffect(() => {
    if (persistedBalanceName && !balanceName && dispenseBooths.length > 0) {
      handleBalanceSelect(persistedBalanceName);
    }
  }, [persistedBalanceName, dispenseBooths]);

  const handleEntrySelect = (prod_dispenses_id) => {
    const entry = previousBatches.find(
      (b) => b.prod_dispenses_id === prod_dispenses_id
    );
    setSelectedEntry(entry);
    setSelectedRowKeys([]);
    setVerificationState({});
    setIsVerified(false);
    form.setFieldsValue({
      product_batch: prod_dispenses_id,
      product_name: entry?.product_name || '',
      process: entry?.process_name || '',
      stage: entry?.stage_name || '',
      next_stage: entry?.next_stage_name || ''
    });

    if (entry) {
      dispatch(
        getContainersForVerification({
          ...(lc_bmr_eqp_id
            ? { lc_bmr_eqp_id }
            : { packing_dispensing_request_id: dispensing_request_id }),
          prod_dispenses_id: entry.prod_dispenses_id,
          previous_batch_number: entry.batch_number,
          process_id: entry.process_id
        })
      );
    }
  };

  // Container QR scan handler: confirms the scanned container belongs to the
  // selected batch+process and matches the row. Gross weight verification is
  // a separate step (Read button + entered weight comparison).
  const handleQrScan = useCallback(
    (result) => {
      if (result?.text && scanningContainerId) {
        const scanned_container_id = result.text.trim();
        setScanModalVisible(false);

        dispatch(
          scanResolveContainer({
            container_id: scanned_container_id,
            previous_batch_number: selectedEntry?.batch_number,
            process_id: selectedEntry?.process_id
          })
        ).then((res) => {
          if (res?.payload?.success) {
            if (scanned_container_id !== scanningContainerId) {
              message.error(
                `Scanned container "${scanned_container_id}" does not match the selected row "${scanningContainerId}".`
              );
              setVerificationState((prev) => ({
                ...prev,
                [scanningContainerId]: {
                  ...prev[scanningContainerId],
                  container_scanned: false
                }
              }));
            } else {
              message.success(`Container ${scanningContainerId} confirmed.`);
              setVerificationState((prev) => ({
                ...prev,
                [scanningContainerId]: {
                  ...prev[scanningContainerId],
                  container_scanned: true
                }
              }));
            }
          } else {
            message.error(
              res?.payload?.message || 'Failed to resolve container.'
            );
          }
          setScanningContainerId(null);
        });
      }
    },
    [scanningContainerId, selectedEntry, dispatch]
  );

  const handleDescriptionCheck = (container_id, checked) => {
    setVerificationState((prev) => ({
      ...prev,
      [container_id]: {
        ...prev[container_id],
        description_verified: checked
      }
    }));
  };

  const openScanner = (container_id) => {
    setScanningContainerId(container_id);
    setScanModalVisible(true);
  };

  // Check if Approve button should be enabled — three independent gates per row:
  // container scanned, gross weight matched (entered/read), description verified.
  const canApprove =
    !!balanceName &&
    selectedRowKeys.length > 0 &&
    selectedRowKeys.every((key) => {
      const v = verificationState[key];
      return (
        v?.container_scanned === true &&
        v?.gross_wt_verified === true &&
        v?.description_verified === true
      );
    });

  const handleApproveClick = () => {
    if (!balanceName) {
      message.warning('Please select a Balance Name before approving.');
      return;
    }
    if (!canApprove) {
      message.warning(
        'Each selected container must have its QR scanned, gross weight matched, and description confirmed.'
      );
      return;
    }
    const uid = uuidv4();
    setVerificationUniqueId(uid);
    setIsVerified(false);
    form2.resetFields();
    setQaModalVisible(true);
  };

  const handleCloseModal = () => {
    setQaModalVisible(false);
    form2.resetFields();
  };

  // Step 1: Initiate verification via /initiate-verification (same as UACR)
  const onFinishModal = async () => {
    try {
      const values = await form2.validateFields();
      const response = await initiateVerification({
        ...values,
        common_unique_id: verificationUniqueId,
        form: 'product_verification'
      });

      if (response?.data?.success === true) {
        message.success('Verification successful');
        setIsVerified(true);
        setQaModalVisible(false);

        // Step 2: Submit split after successful verification (pass QA employee_id + comment before resetting)
        handleSubmitSplit(values.email, values.comment);
        form2.resetFields();
      } else {
        message.error(
          response?.data?.message ||
            response?.error?.data?.message ||
            'Authentication failed'
        );
      }
    } catch (e) {
      console.log('Verification failed:', e);
    }
  };

  // Step 2: Submit the split
  const handleSubmitSplit = (qa_employee_id, qa_comment) => {
    const payload_containers = selectedRowKeys.map((container_id) => {
      const row = containers.find((c) => c.container_id === container_id);
      const v = verificationState[container_id] || {};
      return {
        prod_dispense_label_id: row?.prod_dispense_label_id,
        container_id,
        gross_wt_stored: row?.gross_weight,
        gross_wt_scanned: v.gross_wt_scanned || ''
      };
    });

    const payload = {
      ...(lc_bmr_eqp_id
        ? { lc_bmr_eqp_id: Number(lc_bmr_eqp_id) }
        : { packing_dispensing_request_id: Number(dispensing_request_id) }),
      previous_batch_number: selectedEntry?.batch_number,
      process_id: selectedEntry?.process_id,
      stage_id: selectedEntry?.stage_id || null,
      next_stage_id: selectedEntry?.next_stage_id || null,
      prod_dispenses_id: selectedEntry?.prod_dispenses_id || null,
      qa_employee_id: qa_employee_id || null,
      qa_comment: qa_comment || null,
      balance_name: balanceName || null,
      tolerance_id: tolerance?.id || null,
      containers: payload_containers
    };

    dispatch(submitSplit(payload)).then((res) => {
      if (res?.payload?.success) {
        setSelectedRowKeys([]);
        setVerificationState({});
        setIsVerified(false);
        // Reload containers to show newly verified ones as disabled
        dispatch(
          getContainersForVerification({
            ...(lc_bmr_eqp_id
              ? { lc_bmr_eqp_id }
              : { packing_dispensing_request_id: dispensing_request_id }),
            prod_dispenses_id: selectedEntry?.prod_dispenses_id,
            previous_batch_number: selectedEntry?.batch_number,
            process_id: selectedEntry?.process_id
          })
        );
      } else {
        message.error(res?.payload?.message || 'Submission failed.');
      }
    });
  };

  const handlePrint = () => {
    if (!selectedEntry) {
      message.warning('Please select a product batch first.');
      return;
    }
    setPrintLoading(true);
    dispatch(
      generateVerificationPdf({
        ...(lc_bmr_eqp_id ? { lc_bmr_eqp_id } : { dispensing_request_id }),
        prod_dispenses_id: selectedEntry.prod_dispenses_id
      })
    )
      .then((response) => {
        if (response?.payload?.success) {
          window.open(
            `${process.env.REACT_APP_STORAGE_URL}${response?.payload?.data
              .split('/')
              .pop()}`,
            '_blank'
          );
          message.success(response?.payload?.message);
        } else {
          message.error(response?.payload?.message || 'Unable to generate PDF');
        }
      })
      .finally(() => setPrintLoading(false));
  };

  // Table columns
  const columns = [
    {
      title: 'Container ID',
      dataIndex: 'container_id',
      key: 'container_id',
      width: 130
    },
    {
      title: 'Gross Wt (kg)',
      dataIndex: 'gross_weight',
      key: 'gross_weight',
      width: 120,
      render: (text) => text ?? '-'
    },
    {
      title: 'Verify container',
      key: 'verify_container',
      width: 160,
      render: (_, record) => {
        if (record.already_verified) {
          return (
            <Tag color="green">
              <CheckCircleOutlined />{' '}
              {currentStage === 3
                ? `Split #${record.existing_split_number}`
                : 'Verified'}
            </Tag>
          );
        }
        if (record.consumed_elsewhere) {
          return (
            <Tag color="default">
              Verified in{' '}
              <a
                href={`/product-verification/lc/${record.consumed_by_lc}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#c91761',
                  fontWeight: 600,
                  textDecoration: 'underline'
                }}
              >
                LC #{record.consumed_by_lc}
              </a>
            </Tag>
          );
        }
        const v = verificationState[record.container_id] || {};
        const scanned = v.container_scanned === true;
        return scanned ? (
          <Tag color="green">
            <CheckCircleOutlined /> Container OK
          </Tag>
        ) : (
          <CustomButton
            size="small"
            onClick={() => openScanner(record.container_id)}
          >
            <QrcodeOutlined /> Scan QR
          </CustomButton>
        );
      }
    },
    {
      title: (
        <Space size={4}>
          <span>Gross Wt (kg)</span>
          {tolerance && (
            <Tooltip
              title={`Tolerance: ±${tolerance.tolerance_percentage}% (${
                tolerance.description || tolerance.verification_type
              })`}
            >
              <InfoCircleOutlined style={{ color: '#c91761' }} />
            </Tooltip>
          )}
        </Space>
      ),
      key: 'verify_gross_weight',
      width: 280,
      render: (_, record) => {
        if (record.already_verified) {
          return (
            <Tag color="green">
              <CheckCircleOutlined /> Verified
            </Tag>
          );
        }
        if (record.consumed_elsewhere) {
          return <Tag color="default">Locked</Tag>;
        }
        const v = verificationState[record.container_id] || {};
        const status = v.gross_status;
        return (
          <Space size={6}>
            <InputNumber
              size="small"
              placeholder="Gross Wt"
              value={v.gross_wt_scanned ?? ''}
              onChange={(value) => handleEnteredGrossWeight(record, value)}
              style={{ width: 100 }}
            />
            <CustomButton
              size="small"
              loading={readingForContainer === record.container_id}
              onClick={() => handleReadGrossWeight(record)}
            >
              Read
            </CustomButton>
            {status === 'match' && <Tag color="green">Match</Tag>}
            {status === 'within' && <Tag color="gold">Within tolerance</Tag>}
            {status === 'beyond' && <Tag color="red">Beyond tolerance</Tag>}
          </Space>
        );
      }
    },
    {
      title: 'Verify Description',
      key: 'verify_description',
      width: 140,
      render: (_, record) => {
        if (record.already_verified) {
          return (
            <Tag color="green">
              <CheckCircleOutlined /> Verified
            </Tag>
          );
        }
        if (record.consumed_elsewhere) {
          return <Tag color="default">Locked</Tag>;
        }
        return (
          <Checkbox
            checked={
              verificationState[record.container_id]?.description_verified ||
              false
            }
            onChange={(e) =>
              handleDescriptionCheck(record.container_id, e.target.checked)
            }
          >
            Confirm
          </Checkbox>
        );
      }
    }
  ];

  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.already_verified || record.consumed_elsewhere
    })
  };

  return (
    <Layout sider={false}>
      <Form form={form} layout="vertical">
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              <span>Product Verification</span>
            </Space>
          }
          className={`${styles.cardStyles_approver}`}
          extra={
            <Space>
              {snapshotSource === 'snapshot' && snapshotCapturedAt ? (
                <Tooltip
                  placement="bottom"
                  title="Inputs are locked to the upstream WIPs that existed when QA approved this LC. The dropdown will not reflect WIPs created after that moment."
                >
                  <Tag color="purple">
                    📸 Snapshot from{' '}
                    {new Date(snapshotCapturedAt).toLocaleString()}
                  </Tag>
                </Tooltip>
              ) : null}
              {packingBatchNumber ? (
                <Tag color="blue">Current Batch: {packingBatchNumber}</Tag>
              ) : null}
            </Space>
          }
        >
          {batchesLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Row gutter={24}>
                <Col span={12}>
                  <FormItem
                    label="Product Batch (process - subprocess - lots - Next stage)#"
                    name="product_batch"
                    rules={[
                      { required: true, message: 'Select a product batch' }
                    ]}
                    className={styles.customLable}
                  >
                    <Select
                      showSearch
                      placeholder="Select product batch"
                      optionFilterProp="children"
                      onChange={handleEntrySelect}
                      loading={batchesLoading}
                    >
                      {previousBatches.map((b) => (
                        <Select.Option
                          key={b.prod_dispenses_id}
                          value={b.prod_dispenses_id}
                        >
                          {`${b.batch_number} - ${b.process_name || 'NA'} - ${
                            b.dispense_sub_process_name || 'NA'
                          } - ${b.lot_no || 'NA'} (${
                            b.next_stage_name || 'NA'
                          })`}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    label="Product Name"
                    name="product_name"
                    className={styles.customLable}
                  >
                    <Input disabled placeholder="Auto-filled" />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={8}>
                  <FormItem
                    label="Process"
                    name="process"
                    className={styles.customLable}
                  >
                    <Input disabled placeholder="Auto-filled" />
                  </FormItem>
                </Col>
                <Col span={8}>
                  <FormItem
                    label="Stage"
                    name="stage"
                    className={styles.customLable}
                  >
                    <Input disabled placeholder="Auto-filled" />
                  </FormItem>
                </Col>
                <Col span={8}>
                  <FormItem
                    label="Next Stage"
                    name="next_stage"
                    className={styles.customLable}
                  >
                    <Input disabled placeholder="Auto-filled" />
                  </FormItem>
                </Col>
              </Row>

              {selectedEntry && (
                <>
                  <Row gutter={24} style={{ marginTop: '8px' }}>
                    <Col xs={4} xl={1} span={2} md={2} sm={2}>
                      <CustomButton
                        className={styles.qrcode}
                        style={{ marginTop: '24px' }}
                        onClick={() => setBalanceScanVisible(true)}
                      >
                        <QrcodeOutlined />
                      </CustomButton>
                    </Col>
                    <Col xs={24} xl={11} span={12}>
                      <FormItem
                        label="Balance Name"
                        className={styles.customLable}
                      >
                        <Select
                          placeholder="Select Balance Name"
                          onChange={handleBalanceSelect}
                          value={balanceName}
                          showSearch
                          optionFilterProp="children"
                        >
                          {dispenseBooths
                            ?.filter((b) => b?.bal_type === 'dispense_prod')
                            ?.map((item, index) => (
                              <Select.Option
                                value={item?.balance_name}
                                key={index}
                              >
                                {item?.balance_name}
                              </Select.Option>
                            ))}
                        </Select>
                      </FormItem>
                    </Col>
                  </Row>

                  <div style={{ marginTop: '16px' }}>
                    <h4>
                      Containers {containersLoading && <Spin size="small" />}
                    </h4>
                    <CustomTable
                      columns={columns}
                      dataSource={containers}
                      rowKey="container_id"
                      rowSelection={rowSelection}
                      loading={containersLoading}
                      pagination={false}
                      scroll={{ x: 800 }}
                    />
                  </div>

                  <Row justify="end" style={{ marginTop: '16px', gap: '12px' }}>
                    <CustomButton
                      type="primary"
                      disabled={!canApprove || submitting}
                      onClick={handleApproveClick}
                      loading={submitting}
                    >
                      Approve and Proceed
                    </CustomButton>
                  </Row>
                </>
              )}
            </>
          )}
        </Card>

        <FormItem className="text-center">
          <Space>
            <CustomButton
              className={styles.inwardButton}
              onClick={() => history.back()}
              type="primary"
            >
              Back
            </CustomButton>
            <CustomButton
              className={styles.inwardButton}
              onClick={handlePrint}
              type="primary"
              icon={<PrinterOutlined />}
              loading={printLoading}
              disabled={printLoading || !selectedEntry}
            >
              Print
            </CustomButton>
          </Space>
        </FormItem>
      </Form>

      {/* QR Scanner Modal */}
      {scanModalVisible && (
        <CustomModal
          title={`Scan QR for Container: ${scanningContainerId}`}
          visible={scanModalVisible}
          onCancel={() => {
            setScanModalVisible(false);
            setScanningContainerId(null);
          }}
          footer={null}
          customFooter={false}
          width="400px"
          destroyOnClose
        >
          <div style={{ width: '100%' }}>
            <QrReader
              constraints={{ facingMode: 'environment' }}
              delay={300}
              onResult={(result) => {
                if (result) handleQrScan(result);
              }}
              containerStyle={{ width: '100%' }}
            />
            <p style={{ textAlign: 'center', marginTop: '12px' }}>
              Point camera at the container QR code
            </p>
          </div>
        </CustomModal>
      )}

      {/* Balance Name QR Scanner Modal */}
      {balanceScanVisible && (
        <CustomModal
          title="Scan Balance QR"
          visible={balanceScanVisible}
          onCancel={() => setBalanceScanVisible(false)}
          footer={null}
          customFooter={false}
          width="400px"
          destroyOnClose
        >
          <div style={{ width: '100%' }}>
            <QrReader
              constraints={{ facingMode: 'environment' }}
              delay={300}
              onResult={(result) => {
                if (result) handleBalanceQrScan(result);
              }}
              containerStyle={{ width: '100%' }}
            />
            <p style={{ textAlign: 'center', marginTop: '12px' }}>
              Point camera at the balance QR code
            </p>
          </div>
        </CustomModal>
      )}

      {/* QA Verification Modal — same pattern as UACR */}
      {qaModalVisible ? (
        <CustomModal
          title=""
          visible={qaModalVisible}
          width="50%"
          footer={null}
          className={styles.customModal}
          closable={false}
        >
          <div className={`${styles.table_row}`}>
            <Form form={form2} layout="vertical" onFinish={onFinishModal}>
              <Card
                title="QA Initiate Verification"
                className={styles.CustomPanel}
              >
                <Row gutter={24}>
                  <Col xs={24} xl={12} span={24} md={24} sm={24}>
                    <FormItem
                      label="Verified By (User id)"
                      name="email"
                      rules={[
                        {
                          required: true,
                          message: 'Enter Verifier User Id'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <Input placeholder="Enter Verifier User Id" />
                    </FormItem>
                  </Col>
                  <Col xs={24} xl={12} span={24} md={24} sm={24}>
                    <FormItem
                      label="Password"
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: 'Enter Password'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <Input.Password placeholder="Enter Password" />
                    </FormItem>
                  </Col>
                  <Col xs={24} xl={24} span={24} md={24} sm={24}>
                    <FormItem
                      label="Comment"
                      name="comment"
                      rules={[
                        {
                          required: true,
                          message: 'Enter QA verification comment'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Enter QA verification comment"
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Card>

              <br />
              <FormItem className="text-center">
                <Space>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    size="medium"
                    loading={isVerifying}
                    disabled={isVerifying}
                  >
                    Submit
                  </CustomButton>
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={handleCloseModal}
                    type="primary"
                    ghost
                    size="medium"
                  >
                    Close
                  </CustomButton>
                </Space>
              </FormItem>
            </Form>
          </div>
        </CustomModal>
      ) : (
        ''
      )}
    </Layout>
  );
};

export default ProductVerificationForm;
